import classNames from 'classnames';
import type { ReactNode, RefObject } from 'react';

export const CAMERA_OFF_LABEL_CLASS =
  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex max-w-[85%] flex-col items-center gap-2 text-center text-white text-ui-xs lg:text-body-l font-medium [text-shadow:0_1px_6px_rgba(0,0,0,0.45)] z-[1] pointer-events-none';

function CameraOffIcon({ className }: { className?: string }) {
  return (
    <svg
      className={classNames('shrink-0 drop-shadow-overlay', className)}
      role="presentation"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 16v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
      <path d="M1 1l22 22" />
    </svg>
  );
}

/** Снимает последний кадр видео на canvas с blur. Возвращает false, если кадра ещё нет. */
export function captureBlurredVideoFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  options?: { mirror?: boolean },
): boolean {
  if (!video.videoWidth || !video.videoHeight) return false;

  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
  canvas.style.filter = 'blur(60px)';

  if (options?.mirror) {
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();
  } else {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  }

  return true;
}

type CameraOffBackdropProps = {
  visible: boolean;
  hasCapturedFrame: boolean;
  canvasRef: RefObject<HTMLCanvasElement>;
  className?: string;
  objectFit?: 'cover' | 'contain';
};

/** Фон при выключенной камере: blurred last frame или запасной градиент. */
export function CameraOffBackdrop({
  visible,
  hasCapturedFrame,
  canvasRef,
  className,
  objectFit = 'cover',
}: CameraOffBackdropProps) {
  return (
    <>
      <canvas
        ref={canvasRef}
        className={classNames('w-full h-full absolute top-0 left-0 z-0', className)}
        style={{
          visibility: visible && hasCapturedFrame ? 'visible' : 'hidden',
          objectFit,
        }}
      />
      {visible && !hasCapturedFrame && (
        <div
          className={classNames('absolute inset-0 z-0 overflow-hidden', className)}
          aria-hidden
          data-camera-off-backdrop
        >
          <div
            className="absolute inset-[-25%] bg-[radial-gradient(ellipse_at_35%_40%,#c9bdb4_0%,#8f8984_42%,#3f3c45_100%)]"
            style={{ filter: 'blur(60px)' }}
          />
        </div>
      )}
    </>
  );
}

type CameraOffLabelProps = {
  visible: boolean;
  children: ReactNode;
  className?: string;
  /** Только иконка (маленькие превью): текст остаётся для скринридеров */
  iconOnly?: boolean;
  /** Компактное превью: текст скрыт на узких экранах (не помещается и налезает на плашку с именем) */
  compact?: boolean;
};

/** Подпись при выключенной камере: иконка + текст прямо поверх размытого фона, без плашки. */
export function CameraOffLabel({
  visible,
  children,
  className,
  iconOnly = false,
  compact = false,
}: CameraOffLabelProps) {
  return (
    <div
      className={classNames(CAMERA_OFF_LABEL_CLASS, className)}
      style={{ visibility: visible ? 'visible' : 'hidden' }}
    >
      <CameraOffIcon className={iconOnly ? 'h-6 w-6' : 'h-6 w-6 lg:h-8 lg:w-8'} />
      {iconOnly ? (
        <span className="sr-only">{children}</span>
      ) : compact ? (
        <span className="max-lg:sr-only">{children}</span>
      ) : (
        children
      )}
    </div>
  );
}
